import React, { useState, useEffect } from 'react';
import { UserCheck, UserX, Users, Clock, CheckCircle, XCircle, User, Mail, Building, Phone } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import axios from 'axios';

const UserApprovalTab = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState(null); // 'approve' or 'reject'
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchPendingUsers();
  }, []);

  const fetchPendingUsers = async () => {
    try {
      setLoading(true);
      const adminToken = localStorage.getItem('admin_token');
      
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/users/pending-approval`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      setPendingUsers(response.data);
    } catch (error) {
      console.error('Error fetching pending users:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return 'Invalid date';
    }
  };

  const handleApprovalAction = (user, action) => {
    setSelectedUser(user);
    setApprovalAction(action);
    setShowApprovalModal(true);
    setRejectionReason('');
  };

  const executeApproval = async () => {
    if (!selectedUser || !approvalAction) return;

    try {
      setProcessing(true);
      const adminToken = localStorage.getItem('admin_token');
      
      if (approvalAction === 'approve') {
        await axios.post(
          `${API_BASE_URL}/api/admin/users/${selectedUser.id}/approve`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${adminToken}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else if (approvalAction === 'reject') {
        await axios.post(
          `${API_BASE_URL}/api/admin/users/${selectedUser.id}/reject`,
          { 
            action: 'reject',
            reason: rejectionReason 
          },
          {
            headers: {
              'Authorization': `Bearer ${adminToken}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }

      // Refresh the pending users list
      await fetchPendingUsers();
      
      setShowApprovalModal(false);
      setSelectedUser(null);
      setApprovalAction(null);
      setRejectionReason('');
      
    } catch (error) {
      console.error('Error processing approval:', error);
      alert('Error processing approval. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>Loading pending users...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Card.Header>
          <Card.Title style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <UserCheck size={20} color="#667eea" />
            User Approval Management
            {pendingUsers.length > 0 && (
              <span style={{
                backgroundColor: '#f59e0b',
                color: 'white',
                fontSize: '12px',
                fontWeight: '600',
                padding: '2px 8px',
                borderRadius: '12px',
                marginLeft: '8px'
              }}>
                {pendingUsers.length}
              </span>
            )}
          </Card.Title>
        </Card.Header>
        
        <Card.Content>
          {pendingUsers.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '48px 24px',
              color: '#6b7280'
            }}>
              <Users size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                No Pending Approvals
              </h3>
              <p style={{ fontSize: '14px', margin: 0 }}>
                All users have been processed. New registrations requiring approval will appear here.
              </p>
            </div>
          ) : (
            <div style={{ overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc' }}>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      fontWeight: '600', 
                      fontSize: '14px',
                      color: '#374151',
                      borderBottom: '1px solid #e5e7eb'
                    }}>
                      User Details
                    </th>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'center', 
                      fontWeight: '600', 
                      fontSize: '14px',
                      color: '#374151',
                      borderBottom: '1px solid #e5e7eb'
                    }}>
                      Registration Date
                    </th>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'center', 
                      fontWeight: '600', 
                      fontSize: '14px',
                      color: '#374151',
                      borderBottom: '1px solid #e5e7eb'
                    }}>
                      Status
                    </th>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'center', 
                      fontWeight: '600', 
                      fontSize: '14px',
                      color: '#374151',
                      borderBottom: '1px solid #e5e7eb'
                    }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pendingUsers.map((user) => (
                    <tr key={user.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '16px 12px' }}>
                        <div>
                          <div style={{ 
                            fontSize: '14px', 
                            fontWeight: '600', 
                            color: '#111827',
                            marginBottom: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <User size={14} color="#6b7280" />
                            {user.full_name}
                          </div>
                          <div style={{ 
                            fontSize: '13px', 
                            color: '#6b7280',
                            marginBottom: '2px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <Mail size={12} color="#9ca3af" />
                            {user.email}
                          </div>
                          <div style={{ 
                            fontSize: '13px', 
                            color: '#6b7280',
                            marginBottom: '2px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <Building size={12} color="#9ca3af" />
                            {user.company_name}
                          </div>
                          <div style={{ 
                            fontSize: '13px', 
                            color: '#6b7280',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <Phone size={12} color="#9ca3af" />
                            {user.phone_number}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                        <span style={{ fontSize: '13px', color: '#6b7280' }}>
                          {formatDate(user.created_at)}
                        </span>
                      </td>
                      <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          backgroundColor: '#fef3c7',
                          color: '#92400e',
                          fontSize: '12px',
                          fontWeight: '600',
                          padding: '4px 8px',
                          borderRadius: '12px'
                        }}>
                          <Clock size={12} />
                          Pending
                        </span>
                      </td>
                      <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          <Button
                            variant="primary"
                            size="sm"
                            icon={<CheckCircle size={14} />}
                            onClick={() => handleApprovalAction(user, 'approve')}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            icon={<XCircle size={14} />}
                            onClick={() => handleApprovalAction(user, 'reject')}
                          >
                            Reject
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Content>
      </Card>

      {/* Approval/Rejection Modal */}
      <Modal
        isOpen={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        title={`${approvalAction === 'approve' ? 'Approve' : 'Reject'} User Registration`}
        size="md"
      >
        {selectedUser && (
          <div>
            <div style={{
              backgroundColor: '#f8fafc',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h4 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#111827',
                marginBottom: '8px'
              }}>
                User Details
              </h4>
              <div style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.5' }}>
                <div><strong>Name:</strong> {selectedUser.full_name}</div>
                <div><strong>Email:</strong> {selectedUser.email}</div>
                <div><strong>Company:</strong> {selectedUser.company_name}</div>
                <div><strong>Phone:</strong> {selectedUser.phone_number}</div>
                <div><strong>Registered:</strong> {formatDate(selectedUser.created_at)}</div>
              </div>
            </div>

            {approvalAction === 'approve' && (
              <div style={{
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <CheckCircle size={16} color="#166534" />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#166534' }}>
                    Approve Registration
                  </span>
                </div>
                <p style={{ fontSize: '13px', color: '#166534', margin: 0 }}>
                  This user will be granted access to the application and assigned a trial subscription.
                </p>
              </div>
            )}

            {approvalAction === 'reject' && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px'
                }}>
                  <XCircle size={16} color="#dc2626" />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#dc2626' }}>
                    Reject Registration
                  </span>
                </div>
                <p style={{ fontSize: '13px', color: '#dc2626', marginBottom: '12px' }}>
                  This user will not be granted access to the application.
                </p>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '4px'
                  }}>
                    Reason for rejection (optional):
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Enter reason for rejection..."
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <Button
                variant="secondary"
                onClick={() => setShowApprovalModal(false)}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button
                variant={approvalAction === 'approve' ? 'primary' : 'danger'}
                onClick={executeApproval}
                loading={processing}
                icon={approvalAction === 'approve' ? <CheckCircle size={16} /> : <XCircle size={16} />}
              >
                {processing 
                  ? (approvalAction === 'approve' ? 'Approving...' : 'Rejecting...') 
                  : (approvalAction === 'approve' ? 'Approve User' : 'Reject User')
                }
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UserApprovalTab;